import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  addDoc, query, where, orderBy, limit, serverTimestamp, increment,
  onSnapshot, writeBatch, startAfter, deleteField
} from 'firebase/firestore';
import { db } from './config';

// ============ USER FUNCTIONS (shared users collection) ============

const _userCache = new Map();

export const getUserById = async (userId) => {
  if (_userCache.has(userId)) return _userCache.get(userId);
  const snap = await getDoc(doc(db, 'users', userId));
  const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
  _userCache.set(userId, data);
  return data;
};

export const getUserByUsername = async (username) => {
  const lower = username.toLowerCase();
  for (const u of _userCache.values()) {
    if (u?.username === lower) return u;
  }
  const q = query(collection(db, 'users'), where('username', '==', lower), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const data = { id: snap.docs[0].id, ...snap.docs[0].data() };
  _userCache.set(data.id, data);
  return data;
};

export const updateUserProfile = async (userId, data) => {
  await updateDoc(doc(db, 'users', userId), data);
  _userCache.delete(userId);
};

export const searchUsers = async (term) => {
  const q = query(
    collection(db, 'users'),
    where('username', '>=', term.toLowerCase()),
    where('username', '<=', term.toLowerCase() + ''),
    limit(20)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ============ VIDEO FUNCTIONS ============

export const createVideo = async ({ userId, videoId, videoUrl, thumbnailUrl, caption, duration, link, tag, privacy }) => {
  const data = {
    userId, videoUrl, thumbnailUrl, caption, duration,
    likesCount: 0, commentsCount: 0, viewsCount: 0,
    privacy: privacy || 'public',
    createdAt: serverTimestamp(),
  };
  if (link) data.link = link;
  if (tag) data.tag = tag;
  await setDoc(doc(db, 'videos', videoId), data);
  return videoId;
};

export const searchByHashtag = async (tag) => {
  const lower = tag.toLowerCase().replace(/^#/, '').trim();
  if (!lower) return { users: [], videos: [] };
  const [newTagsSnap, oldTagSnap, videosSnap] = await Promise.all([
    getDocs(query(collection(db, 'users'), where('profileTags', 'array-contains', lower), limit(20))),
    getDocs(query(collection(db, 'users'), where('profileTag', '==', lower), limit(20))),
    getDocs(query(collection(db, 'videos'), where('tag', '==', lower), limit(20))),
  ]);
  const usersById = new Map();
  [...newTagsSnap.docs, ...oldTagSnap.docs].forEach(d => usersById.set(d.id, { id: d.id, ...d.data() }));
  return {
    users: [...usersById.values()],
    videos: videosSnap.docs.map(d => ({ id: d.id, ...d.data() })),
  };
};

export const getVideoById = async (videoId) => {
  const snap = await getDoc(doc(db, 'videos', videoId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getPublicVideos = async (limitCount = 10, lastDoc = null) => {
  let q = query(collection(db, 'videos'), orderBy('createdAt', 'desc'), limit(limitCount));
  if (lastDoc) q = query(collection(db, 'videos'), orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(limitCount));
  const snap = await getDocs(q);
  return {
    videos: snap.docs.map(d => ({ id: d.id, ...d.data() })),
    lastDoc: snap.docs[snap.docs.length - 1] || null
  };
};

export const getFollowingVideos = async (userId, limitCount = 10, lastDoc = null) => {
  const followIds = await getTapeFollowingIds(userId);
  if (followIds.length === 0) return { videos: [], lastDoc: null };

  const userIds = [userId, ...followIds].slice(0, 30);
  let q = query(collection(db, 'videos'), where('userId', 'in', userIds), orderBy('createdAt', 'desc'), limit(limitCount));
  if (lastDoc) q = query(collection(db, 'videos'), where('userId', 'in', userIds), orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(limitCount));
  const snap = await getDocs(q);
  return {
    videos: snap.docs.map(d => ({ id: d.id, ...d.data() })),
    lastDoc: snap.docs[snap.docs.length - 1] || null
  };
};

export const getVideosByUser = async (userId) => {
  const q = query(collection(db, 'videos'), where('userId', '==', userId), limit(30));
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
};

export const deleteVideo = async (videoId) => {
  await deleteDoc(doc(db, 'videos', videoId));
};

export const updateVideo = async (videoId, { caption, link, tag, privacy }) => {
  const updates = { caption };
  updates.link = link ? link : deleteField();
  updates.tag = tag ? tag : deleteField();
  if (privacy) updates.privacy = privacy;
  await updateDoc(doc(db, 'videos', videoId), updates);
};

export const incrementViews = async (videoId) => {
  await updateDoc(doc(db, 'videos', videoId), { viewsCount: increment(1) });
};

// ============ LIKE FUNCTIONS ============

export const likeVideo = async (userId, videoId, videoOwnerId) => {
  await setDoc(doc(db, 'tape_likes', `${userId}_${videoId}`), { userId, videoId, createdAt: serverTimestamp() });
  await updateDoc(doc(db, 'videos', videoId), { likesCount: increment(1) });
  if (userId !== videoOwnerId) {
    await createTapeNotification(videoOwnerId, userId, 'like', videoId);
  }
};

export const unlikeVideo = async (userId, videoId) => {
  await deleteDoc(doc(db, 'tape_likes', `${userId}_${videoId}`));
  await updateDoc(doc(db, 'videos', videoId), { likesCount: increment(-1) });
};

export const hasUserLikedVideo = async (userId, videoId) => {
  const snap = await getDoc(doc(db, 'tape_likes', `${userId}_${videoId}`));
  return snap.exists();
};

export const getUserLikedVideos = async (userId) => {
  const q = query(collection(db, 'tape_likes'), where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data().videoId);
};

// ============ COMMENT FUNCTIONS ============

export const addComment = async (videoId, userId, text, videoOwnerId) => {
  const ref = await addDoc(collection(db, 'tape_comments'), {
    videoId, userId, text, createdAt: serverTimestamp()
  });
  await updateDoc(doc(db, 'videos', videoId), { commentsCount: increment(1) });
  if (userId !== videoOwnerId) {
    await createTapeNotification(videoOwnerId, userId, 'comment', videoId);
  }
  return ref.id;
};

export const getComments = async (videoId, limitCount = 50) => {
  const q = query(
    collection(db, 'tape_comments'),
    where('videoId', '==', videoId),
    orderBy('createdAt', 'asc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteComment = async (commentId, videoId) => {
  await deleteDoc(doc(db, 'tape_comments', commentId));
  await updateDoc(doc(db, 'videos', videoId), { commentsCount: increment(-1) });
};

// ============ FOLLOW FUNCTIONS (tape_follows — separate from JPEGS) ============

export const tapFollowUser = async (followerId, followingId) => {
  await setDoc(doc(db, 'tape_follows', `${followerId}_${followingId}`), {
    followerId, followingId, createdAt: serverTimestamp()
  });
  await createTapeNotification(followingId, followerId, 'follow', null);
};

export const tapeUnfollowUser = async (followerId, followingId) => {
  await deleteDoc(doc(db, 'tape_follows', `${followerId}_${followingId}`));
};

export const isTapeFollowing = async (followerId, followingId) => {
  const snap = await getDoc(doc(db, 'tape_follows', `${followerId}_${followingId}`));
  return snap.exists();
};

export const getTapeFollowerCount = async (userId) => {
  const q = query(collection(db, 'tape_follows'), where('followingId', '==', userId));
  const snap = await getDocs(q);
  return snap.size;
};

export const getTapeFollowingCount = async (userId) => {
  const q = query(collection(db, 'tape_follows'), where('followerId', '==', userId));
  const snap = await getDocs(q);
  return snap.size;
};

export const getTapeFollowingIds = async (userId) => {
  const q = query(collection(db, 'tape_follows'), where('followerId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data().followingId);
};

// ============ NOTIFICATION FUNCTIONS ============

export const createTapeNotification = async (recipientId, senderId, type, videoId) => {
  if (recipientId === senderId) return;
  await addDoc(collection(db, 'tape_notifications'), {
    recipientId, senderId, type, videoId: videoId || null,
    read: false, createdAt: serverTimestamp()
  });
};

export const getTapeNotifications = async (userId, limitCount = 50) => {
  const q = query(
    collection(db, 'tape_notifications'),
    where('recipientId', '==', userId),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
};

export const markTapeNotificationRead = async (id) => {
  await updateDoc(doc(db, 'tape_notifications', id), { read: true });
};

export const markAllTapeNotificationsRead = async (userId) => {
  const q = query(collection(db, 'tape_notifications'), where('recipientId', '==', userId), where('read', '==', false));
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.update(d.ref, { read: true }));
  await batch.commit();
};

export const subscribeToTapeNotifications = (userId, onUpdate, onError) => {
  const q = query(
    collection(db, 'tape_notifications'),
    where('recipientId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  return onSnapshot(q, (snap) => onUpdate(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError);
};

export const subscribeToTapeUnreadCount = (userId, onUpdate, onError) => {
  const q = query(collection(db, 'tape_notifications'), where('recipientId', '==', userId), where('read', '==', false));
  return onSnapshot(q, (snap) => onUpdate(snap.size), onError);
};

// ============ PRIVATE LIST FUNCTIONS ============

export const getTapeFollowers = async (userId) => {
  const q = query(collection(db, 'tape_follows'), where('followingId', '==', userId));
  const snap = await getDocs(q);
  const ids = snap.docs.map(d => d.data().followerId);
  const users = await Promise.all(ids.map(id => getUserById(id)));
  return users.filter(Boolean);
};

export const getPrivateList = async (ownerId) => {
  const q = query(collection(db, 'tape_private_list'), where('ownerId', '==', ownerId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data().followerId);
};

export const isOnPrivateList = async (ownerId, followerId) => {
  const snap = await getDoc(doc(db, 'tape_private_list', `${ownerId}_${followerId}`));
  return snap.exists();
};

export const addToPrivateList = async (ownerId, followerId) => {
  await setDoc(doc(db, 'tape_private_list', `${ownerId}_${followerId}`), {
    ownerId, followerId, createdAt: serverTimestamp(),
  });
  await createTapeNotification(followerId, ownerId, 'private_list_added', null);
};

export const removeFromPrivateList = async (ownerId, followerId) => {
  await deleteDoc(doc(db, 'tape_private_list', `${ownerId}_${followerId}`));
};

export const getPrivateAccessOwnerIds = async (userId) => {
  const q = query(collection(db, 'tape_private_list'), where('followerId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data().ownerId);
};

// ============ MESSAGING FUNCTIONS ============

export const getOrCreateConversation = async (uid1, uid2) => {
  const [a, b] = [uid1, uid2].sort();
  const convId = `${a}_${b}`;
  const ref = doc(db, 'tape_conversations', convId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      participants: [a, b],
      unreadCounts: { [a]: 0, [b]: 0 },
      lastMessage: null,
      updatedAt: serverTimestamp(),
    });
    return { id: convId, participants: [a, b], unreadCounts: { [a]: 0, [b]: 0 }, lastMessage: null };
  }
  return { id: convId, ...snap.data() };
};

export const getConversationById = async (convId) => {
  const snap = await getDoc(doc(db, 'tape_conversations', convId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const sendMessage = async (convId, senderId, text) => {
  const convRef = doc(db, 'tape_conversations', convId);
  const snap = await getDoc(convRef);
  if (!snap.exists()) return;
  const otherId = snap.data().participants.find(p => p !== senderId);
  await addDoc(collection(db, 'tape_conversations', convId, 'messages'), {
    senderId, text: text.trim(), createdAt: serverTimestamp(),
  });
  await updateDoc(convRef, {
    lastMessage: { text: text.trim(), senderId, createdAt: serverTimestamp() },
    updatedAt: serverTimestamp(),
    [`unreadCounts.${otherId}`]: increment(1),
  });
};

export const subscribeToMessages = (convId, onUpdate, onError) => {
  const q = query(
    collection(db, 'tape_conversations', convId, 'messages'),
    orderBy('createdAt', 'asc'),
    limit(100)
  );
  return onSnapshot(q, snap => onUpdate(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError);
};

export const markConversationRead = async (convId, userId) => {
  await updateDoc(doc(db, 'tape_conversations', convId), {
    [`unreadCounts.${userId}`]: 0,
  });
};

export const subscribeToConversations = (userId, onUpdate, onError) => {
  const q = query(
    collection(db, 'tape_conversations'),
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc'),
    limit(30)
  );
  return onSnapshot(q, snap => onUpdate(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError);
};

export const subscribeToUnreadMessageCount = (userId, onUpdate, onError) => {
  const q = query(collection(db, 'tape_conversations'), where('participants', 'array-contains', userId));
  return onSnapshot(q, snap => {
    let total = 0;
    snap.docs.forEach(d => { total += d.data().unreadCounts?.[userId] || 0; });
    onUpdate(total);
  }, onError);
};
